import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import UnitOpLinkingFlowchartEditor from 'studies/typed/process-development/unit-op-linking-flowchart/UnitOpLinkingFlowchartEditor';
import {
  SuccessfulQueryUnitOpLinkingState,
  NotSuccessfulQueryUnitOpLinkingState,
  UnitOpLinkingStateStatus,
} from 'studies/typed/process-development/unit-op-linking-flowchart/useUnitOpLinkingState';
import {
  StudyInaccessibleContentBanner,
  StudyQueryErrorBanner,
} from 'studies/typed/utils/ui/StudyQueryBanners';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {assertNever} from 'util/Types';

/**
 * Props for the QueryErrorHandlingUnitOpLinkingEditor component.
 * `destinationPMEIs` may optionally be specified here for new destinations - for example if destination PMEIs
 * are determined after the useUnitOpLinkingState hook is called, as is the case when the user is creating a
 * new experiment and the destination PMEIs are determined after the hook in the AddExperimentModal.
 */
interface QueryErrorHandlingUnitOpLinkingEditorProps {
  unitOpLinkingState: SuccessfulQueryUnitOpLinkingState | NotSuccessfulQueryUnitOpLinkingState;
}

/**
 * Renders the UnitOpLinkingFlowchartEditor with data from the useUnitOpLinkingState hook. Renders error
 * or loading banners as needed if the query in the hook fails or has not yet completed. This component is
 * used both during experiment creation and after experiment creation.
 */
export default function QueryErrorHandlingUnitOpLinkingEditor(
  props: QueryErrorHandlingUnitOpLinkingEditorProps
): JSX.Element {
  const {unitOpLinkingState} = props;

  if (unitOpLinkingState.status === UnitOpLinkingStateStatus.QUERY_ERROR) {
    return <StudyQueryErrorBanner />;
  } else if (unitOpLinkingState.status === UnitOpLinkingStateStatus.QUERY_PENDING) {
    return (
      <>
        <div className={css(styles.loadingDiv)} />
        <CenteredHorizontalThrobber />
      </>
    );
  } else if (unitOpLinkingState.status === UnitOpLinkingStateStatus.STUDY_INACCESSIBLE) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={unitOpLinkingState.inaccessibleStudy}
        modelType={ModelTypeConstants.STUDY}
        displayErrorText="This study is no longer accessible. Try refreshing the page if this is unexpected."
      />
    );
  } else if (unitOpLinkingState.status === UnitOpLinkingStateStatus.SUCCESS) {
    // Array of the initial links that were already present in the flowchart when the user opened
    // the editor and new links that the user has added or removed in the flowchart editor.
    const allLinks = [...unitOpLinkingState.initialLinks, ...(unitOpLinkingState.newLinks ?? [])];
    return (
      <div className={css(styles.flowchartContainer)}>
        <UnitOpLinkingFlowchartEditor
          sourcePMEIs={unitOpLinkingState.sourcePMEIs}
          destinationPMEIs={unitOpLinkingState.destinationPMEIs}
          initialLinks={allLinks}
          onChangeLinks={unitOpLinkingState.setNewLinks}
        />
      </div>
    );
  } else {
    assertAndContinue(false, `Unexpected status ${assertNever(unitOpLinkingState.status)}`);
    return <></>;
  }
}

const styles = StyleSheet.create({
  loadingDiv: {
    paddingBottom: 5,
  },
  flowchartContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
});
